const toFiniteNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

export const getAccessibleCompanyNames = (companies) =>
    [
        ...new Set(
            (companies || [])
                .map((company) =>
                    typeof company === "string" ? company : company?.name
                )
                .filter(Boolean)
        ),
    ].sort((left, right) => left.localeCompare(right));

// A company badge has one selling scope. Never sum different POS warehouses
// or guess which profile will be selected when a company has multiple scopes.
export const getRetailSellingWarehouses = (profiles) => {
    const byCompany = new Map();
    (profiles || []).forEach((profile) => {
        if (Number(profile?.disabled) === 1 || !profile?.company || !profile?.warehouse) return;
        if (!byCompany.has(profile.company)) byCompany.set(profile.company, new Set());
        byCompany.get(profile.company).add(profile.warehouse);
    });
    return [...byCompany.entries()]
        .filter(([, warehouses]) => warehouses.size === 1)
        .map(([company, warehouses]) => ({ company, name: [...warehouses][0] }));
};

export const buildRetailWarehouseScope = ({
    warehouses,
    currentWarehouse,
    currentCompany,
}) => {
    const warehouseCompanyByName = (warehouses || []).reduce(
        (result, warehouse) => {
            const name = String(warehouse?.name || "").trim();
            const company = String(warehouse?.company || "").trim();
            if (name && company && company !== currentCompany) result[name] = company;
            return result;
        },
        {}
    );

    if (currentWarehouse && currentCompany && !warehouseCompanyByName[currentWarehouse]) {
        warehouseCompanyByName[currentWarehouse] = currentCompany;
    }

    return {
        warehouseCompanyByName,
        warehouseNames: Object.keys(warehouseCompanyByName).sort((left, right) =>
            left.localeCompare(right)
        ),
    };
};

const addBinQuantity = (current, row) => ({
    item_code: row.item_code,
    warehouse: row.warehouse,
    stock_uom: row.stock_uom || current?.stock_uom,
    actual_qty:
        toFiniteNumber(current?.actual_qty) + toFiniteNumber(row.actual_qty),
    projected_qty:
        toFiniteNumber(current?.projected_qty) +
        toFiniteNumber(row.projected_qty),
    reserved_qty:
        toFiniteNumber(current?.reserved_qty) +
        toFiniteNumber(row.reserved_qty),
});

export const buildRetailStockIndex = ({
    binRows,
    warehouseCompanyByName,
    currentWarehouse,
}) =>
    (binRows || []).reduce(
        (index, row) => {
            if (!row?.item_code) return index;

            if (currentWarehouse && row.warehouse === currentWarehouse) {
                index.currentWarehouseByItemCode[row.item_code] = addBinQuantity(
                    index.currentWarehouseByItemCode[row.item_code],
                    row
                );
            }

            const company = warehouseCompanyByName?.[row.warehouse];
            if (company) {
                if (!index.companyActualQtyByItemCode[row.item_code]) {
                    index.companyActualQtyByItemCode[row.item_code] = {};
                }
                const companyQuantities =
                    index.companyActualQtyByItemCode[row.item_code];
                companyQuantities[company] =
                    toFiniteNumber(companyQuantities[company]) +
                    toFiniteNumber(row.available_qty ?? row.actual_qty);
            }

            return index;
        },
        {
            currentWarehouseByItemCode: {},
            companyActualQtyByItemCode: {},
        }
    );

export const getOtherCompanyStockEntries = (
    companyActualQty,
    currentCompany
) =>
    Object.entries(companyActualQty || {})
        .filter(([company]) => company && company !== currentCompany)
        .map(([company, quantity]) => ({
            company,
            quantity: toFiniteNumber(quantity),
        }))
        .sort((left, right) => left.company.localeCompare(right.company));

export const formatStockQuantity = (quantity) => {
    const number = toFiniteNumber(quantity);
    return Number.isInteger(number)
        ? String(number)
        : number.toFixed(2).replace(/\.?0+$/, "");
};

const isMainRetailDraftSaleItem = (row) =>
    !row?.parent_item || row.parent_item === "NULL";

export const buildRetailDraftSalesQuantityIndex = (rows) =>
    (rows || []).reduce((result, row) => {
        if (!row?.item_code || !isMainRetailDraftSaleItem(row)) return result;

        result[row.item_code] =
            toFiniteNumber(result[row.item_code]) + toFiniteNumber(row.qty);
        return result;
    }, {});

export const buildRetailDraftOrderFilters = ({ tableName, company } = {}) => {
    const normalizedTableName = String(tableName || "").trim();
    const normalizedCompany = String(company || "").trim();
    if (!normalizedTableName || !normalizedCompany) return [];

    return [
        ["status", "=", "Attending"],
        ["table", "=", normalizedTableName],
        ["company", "=", normalizedCompany],
        ["docstatus", "!=", 2],
    ];
};

export const getRetailDraftSalesQuantity = (item) =>
    toFiniteNumber(item?.retail_draft_sales_qty);

export const getRetailAvailableQuantity = (item) => {
    const explicitQuantity = Number(item?.retail_available_qty);
    if (Number.isFinite(explicitQuantity)) return explicitQuantity;

    const sourceQuantity =
        item?.actual_qty ?? item?.projected_qty ?? item?.pos_qty;
    const numericSourceQuantity = Number(sourceQuantity);
    if (!Number.isFinite(numericSourceQuantity)) return sourceQuantity;

    return numericSourceQuantity - getRetailDraftSalesQuantity(item);
};
